import { DateTime } from "luxon";
import { useEffect, useMemo } from "react";
import type { ScheduleChangesWindowPreset } from "../components/scheduleChanges/types";
import { useAuth } from "../contexts/AuthProvider";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import {
	buildWindowPresets,
	commitLastVisitWindow,
	type LastVisitStorageError,
	type LastVisitWindowInput,
	resolveLastVisitWindow,
	type StorageLike,
} from "../utils/lastVisitWindow";
import { useLatestRef } from "./useLatestRef";

/**
 * The stores, reached through on every call: in some browsers merely reading `window.localStorage` throws when storage
 * is blocked, so the access itself has to sit inside `lastVisitWindow`'s try/catch rather than here at module load.
 */
const LOCAL_STORAGE: StorageLike = {
	getItem: (key) => window.localStorage.getItem(key),
	setItem: (key, value) => window.localStorage.setItem(key, value),
};
const SESSION_STORAGE: StorageLike = {
	getItem: (key) => window.sessionStorage.getItem(key),
	setItem: (key, value) => window.sessionStorage.setItem(key, value),
};

export type LastVisitWindowState = {
	/** Where "since my last visit" starts — fixed for the whole browser session (see `utils/lastVisitWindow`). */
	since: DateTime;
	/** The visit this session started from, or null on the first visit from this device. */
	lastVisit: DateTime | null;
	/** "Since my last visit", "Last 2 days", "Last week". */
	presets: readonly ScheduleChangesWindowPreset[];
};

/**
 * The signed-in user's "since my last visit" window in the chosen department, for the schedule-changes surfaces. Null
 * until a user and a department are loaded, and while `enabled` is false — then nothing is read or stored (staff never
 * get a last visit).
 *
 * Storage failures never break the page: they are tracked as `schedule_changes_last_visit_storage_error` and the
 * window falls back to what could be read.
 *
 * Mount it ONCE (the `ScheduleChangesProvider` does): the first mount in a session writes the new last visit.
 */
export const useLastVisitWindow = (enabled: boolean): LastVisitWindowState | null => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const { trackError } = useAnalytics();
	// Read in the effect, not a dependency: a new `trackError` (analytics context changed) must not re-commit or re-report.
	const trackErrorRef = useLatestRef(trackError);
	const departmentId = chosenDepartment.department_id;
	const timezone = chosenDepartment.timezone;
	// Typed `string`, but the signed-out placeholder user has no id at all.
	const userId: string | undefined = userInfo.id;

	// Resolved once per user and department: `now` is captured here, so the window does not creep forward on every render.
	const resolution = useMemo(() => {
		if (!enabled || userId === undefined || userId === "" || departmentId === "" || timezone === "") return null;
		const input: LastVisitWindowInput = {
			userId,
			departmentId,
			timezone,
			now: DateTime.now().setZone(timezone),
			local: LOCAL_STORAGE,
			session: SESSION_STORAGE,
		};
		return { input, resolved: resolveLastVisitWindow(input) };
	}, [enabled, userId, departmentId, timezone]);

	useEffect(() => {
		if (resolution === null) return;
		const { input, resolved } = resolution;
		const errors: LastVisitStorageError[] = [...resolved.storageErrors, ...commitLastVisitWindow(resolved, input)];
		for (const { operation, error } of errors) {
			trackErrorRef.current("schedule_changes_last_visit_storage_error", {
				department_id: input.departmentId,
				operation,
				error_message: error instanceof Error ? error.message : String(error),
			});
		}
	}, [resolution, trackErrorRef]);

	return useMemo(() => {
		if (resolution === null) return null;
		const { resolved } = resolution;
		return { since: resolved.since, lastVisit: resolved.lastVisit, presets: buildWindowPresets(resolved) };
	}, [resolution]);
};
